import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Download, FileText, Check, ArrowLeft } from 'lucide-react';
import jsPDF from 'jspdf';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Button } from '../../components/ui/button';
import LegalLayout, { LegalH2, LegalP } from './LegalLayout';

/* Build the PDF formulario de desistimiento */
const buildPdf = ({ nombre, email, fechaContratacion, producto, motivo }) => {
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const today = new Date().toLocaleDateString('es-ES', {
    year: 'numeric', month: 'long', day: 'numeric'
  });

  // Header
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.text('Formulario de desistimiento', 20, 25);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(90, 80, 60);
  doc.text('Modelo conforme al Real Decreto Legislativo 1/2007 (TRLGDCU)', 20, 32);
  doc.setTextColor(0, 0, 0);

  // Destinatario block
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.text('A la atención de:', 20, 48);
  doc.setFont('helvetica', 'normal');
  const destinatario = [
    'Herminio Vázquez Vigón',
    'Autónomo persona física',
    'Domicilio: Barcelona (España)',
    'Email: hola@herminiovazquez.com',
  ];
  destinatario.forEach((line, i) => {
    doc.text(line, 20, 55 + i * 6);
  });

  // Cuerpo
  const y0 = 95;
  doc.setFont('helvetica', 'bold');
  doc.text('Comunicación de desistimiento', 20, y0);
  doc.setFont('helvetica', 'normal');

  const cuerpo = [
    'Por la presente le comunico que desisto del contrato celebrado para la',
    'prestación del siguiente servicio digital:',
    '',
    `Producto/servicio:  ${producto || '___________________________________________'}`,
    `Fecha de contratación:  ${fechaContratacion || '____ / ____ / ________'}`,
    '',
    'Datos de la persona consumidora:',
    `  · Nombre y apellidos:  ${nombre || '___________________________________________'}`,
    `  · Email de la cuenta:  ${email || '___________________________________________'}`,
    '',
    ...(motivo ? ['Motivo (opcional):', wrapText(doc, motivo, 170), ''] : []),
    `Fecha de esta comunicación:  ${today}`,
    '',
    'Firma de la persona consumidora (si se envía en papel):',
    '',
    '',
    '_______________________________',
  ].flat();

  let y = y0 + 10;
  doc.setFontSize(11);
  cuerpo.forEach(line => {
    if (typeof line === 'string') {
      doc.text(line, 20, y);
      y += 6;
    }
  });

  // Footer
  doc.setFontSize(9);
  doc.setTextColor(120, 110, 90);
  doc.text(
    'Puedes enviar este formulario firmado por email a hola@herminiovazquez.com.',
    20, 275
  );
  doc.text(
    'Recibirás el reembolso íntegro en un plazo máximo de 14 días naturales por el mismo medio de pago utilizado.',
    20, 280
  );

  doc.save('formulario-desistimiento.pdf');
};

/* Naive word wrap for jsPDF */
function wrapText(doc, text, maxWidth) {
  return doc.splitTextToSize(text, maxWidth);
}

const Desistimiento = () => {
  const [formData, setFormData] = useState({
    nombre: '', email: '', fechaContratacion: '', producto: '', motivo: '',
  });
  const [generated, setGenerated] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleDownload = (e) => {
    e.preventDefault();
    buildPdf(formData);
    setGenerated(true);
    setTimeout(() => setGenerated(false), 3500);
  };

  const handleDownloadBlank = () => {
    buildPdf({});
    setGenerated(true);
    setTimeout(() => setGenerated(false), 3500);
  };

  return (
    <LegalLayout
      title="Formulario de desistimiento"
      subtitle="Descarga en PDF el modelo oficial para ejercer tu derecho de desistimiento sobre una compra realizada en la escuela."
      lastUpdate="Abril 2026"
    >
      <LegalH2>¿Qué es esto?</LegalH2>
      <LegalP>
        Si has comprado una formación o te has suscrito al Campus y quieres desistir del contrato dentro del plazo legal de 14 días, este formulario te facilita la comunicación formal. No es obligatorio usarlo: basta con un email a <a href="mailto:hola@herminiovazquez.com" className="text-[#c9a227] hover:underline">hola@herminiovazquez.com</a> con el asunto "Desistimiento" indicando tus datos. Pero si prefieres algo más formal, aquí tienes el modelo.
      </LegalP>
      <LegalP>
        Consulta las condiciones completas, excepciones aplicables y garantía comercial voluntaria en los{' '}
        <Link to="/terminos#desistimiento" className="text-[#c9a227] hover:underline">Términos y condiciones</Link>.
      </LegalP>

      <LegalH2>Opción 1 — Descargar el modelo en blanco</LegalH2>
      <LegalP>
        PDF listo para imprimir, rellenar a mano, firmar y enviarnos por email o correo postal:
      </LegalP>
      <div className="my-4">
        <Button
          onClick={handleDownloadBlank}
          className="bg-[#faf6ed] text-[#2a2010] border border-[#e0d4b0] hover:bg-[#f4ead0] rounded-full px-6 py-6 flex items-center gap-2 font-sans"
          data-testid="download-blank-pdf"
        >
          <FileText className="w-4 h-4" strokeWidth={1.5} />
          Descargar modelo en blanco (PDF)
        </Button>
      </div>

      <LegalH2>Opción 2 — Rellenar online y descargar ya cumplimentado</LegalH2>
      <LegalP>
        Más rápido: completa estos campos y descargaremos el PDF con tus datos listos.
      </LegalP>

      <form onSubmit={handleDownload} className="mt-6 space-y-5 p-6 rounded-2xl bg-[#fdf9f0] border border-[#e8d9a8]" data-testid="desistimiento-form">
        <div>
          <Label htmlFor="des-nombre" className="text-sm font-sans font-medium text-[#2a2010]">Nombre y apellidos *</Label>
          <Input
            id="des-nombre" name="nombre" required
            value={formData.nombre} onChange={handleChange}
            className="mt-1 bg-white border-[#e0d4b0]"
            data-testid="des-nombre"
          />
        </div>
        <div>
          <Label htmlFor="des-email" className="text-sm font-sans font-medium text-[#2a2010]">Email de la cuenta *</Label>
          <Input
            id="des-email" name="email" type="email" required
            value={formData.email} onChange={handleChange}
            className="mt-1 bg-white border-[#e0d4b0]"
            data-testid="des-email"
          />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="des-fecha" className="text-sm font-sans font-medium text-[#2a2010]">Fecha de contratación *</Label>
            <Input
              id="des-fecha" name="fechaContratacion" type="date" required
              value={formData.fechaContratacion} onChange={handleChange}
              className="mt-1 bg-white border-[#e0d4b0]"
              data-testid="des-fecha"
            />
          </div>
          <div>
            <Label htmlFor="des-producto" className="text-sm font-sans font-medium text-[#2a2010]">Producto o suscripción *</Label>
            <Input
              id="des-producto" name="producto" required placeholder="Ej: Curso de Límites, Campus mensual…"
              value={formData.producto} onChange={handleChange}
              className="mt-1 bg-white border-[#e0d4b0]"
              data-testid="des-producto"
            />
          </div>
        </div>
        <div>
          <Label htmlFor="des-motivo" className="text-sm font-sans font-medium text-[#2a2010]">Motivo (opcional)</Label>
          <textarea
            id="des-motivo" name="motivo" rows={3}
            value={formData.motivo} onChange={handleChange}
            className="mt-1 w-full bg-white border border-[#e0d4b0] rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:border-[#c9a227] transition-colors"
            data-testid="des-motivo"
          />
          <p className="text-xs text-[#8a7a60] mt-1">No estás obligada/o a indicar el motivo. El derecho de desistimiento es incondicional durante los 14 días.</p>
        </div>

        <Button
          type="submit"
          className="bg-[#c9a227] hover:bg-[#b89220] text-white w-full py-6 rounded-full font-sans font-medium shadow-sm hover:shadow-md transition-all flex items-center justify-center gap-2"
          data-testid="des-generate-pdf"
        >
          {generated ? (
            <>
              <Check className="w-4 h-4" strokeWidth={2} />
              PDF descargado
            </>
          ) : (
            <>
              <Download className="w-4 h-4" strokeWidth={1.5} />
              Generar y descargar PDF
            </>
          )}
        </Button>
      </form>

      <LegalH2>¿Y ahora qué?</LegalH2>
      <LegalP>
        Envía el PDF firmado (o el email equivalente) a <a href="mailto:hola@herminiovazquez.com" className="text-[#c9a227] hover:underline">hola@herminiovazquez.com</a>. Recibirás el reembolso íntegro por el mismo medio de pago en un plazo máximo de 14 días naturales desde que recibamos tu solicitud.
      </LegalP>
      <LegalP>
        Si prefieres hablarlo antes — quizá podemos entender qué no funcionó — escríbenos sin compromiso. No hace falta justificarse, pero siempre escuchamos.
      </LegalP>

      <div className="mt-10">
        <Link to="/terminos" className="inline-flex items-center gap-2 text-sm text-[#c9a227] hover:underline">
          <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
          Volver a los Términos y condiciones
        </Link>
      </div>
    </LegalLayout>
  );
};

export default Desistimiento;
